/**
 * LinksPage — tracked redirect links for non-Google channels (Reddit,
 * email, social). Shows the shareable /v1/adopt/r/<token> URL per link.
 */

import { useState } from 'react';
import { API_BASE } from '@/lib/api';
import {
  useAdoptLinks,
  useCreateAdoptLink,
  useArchiveAdoptLink,
} from '../hooks/useAdvertisingOptimization';

interface Props {
  holderId: string;
}

export function LinksPage({ holderId }: Props) {
  const linksQuery = useAdoptLinks(holderId);
  const createLink = useCreateAdoptLink(holderId);
  const archiveLink = useArchiveAdoptLink(holderId);

  const [destination, setDestination] = useState('');
  const [channel, setChannel] = useState('');
  const [copied, setCopied] = useState<string | null>(null);

  // Redirect URLs live on the API origin (clicks never touch the SPA).
  const redirectBase = API_BASE;

  const create = async (): Promise<void> => {
    if (!destination.startsWith('https://')) return;
    await createLink.mutateAsync({
      destinationUrl: destination,
      ...(channel.trim() ? { channel: channel.trim().toLowerCase() } : {}),
    });
    setDestination('');
    setChannel('');
  };

  const copy = async (token: string): Promise<void> => {
    const url = `${redirectBase}/adopt/r/${token}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(token);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      // clipboard unavailable — the URL is visible in the table anyway
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto flex flex-col gap-6">
      <header>
        <h1 className="text-2xl font-semibold">Tracked links</h1>
        <p className="text-sm text-muted-foreground">
          Redirect links that log every click with campaign context. Use these for Reddit, email,
          and social placements. (Google Ads uses parallel tracking instead — no redirect allowed.)
        </p>
      </header>

      <section className="rounded-lg border border-border p-4 flex flex-col gap-3">
        <h2 className="font-medium">New link</h2>
        <div className="flex gap-2">
          <input
            className="flex-1 rounded-md border border-border bg-background px-3 py-2 text-sm"
            value={destination}
            onChange={(e) => setDestination(e.target.value)}
            placeholder="https://fias.io/a/arche_image_editor"
          />
          <input
            className="w-32 rounded-md border border-border bg-background px-3 py-2 text-sm"
            value={channel}
            onChange={(e) => setChannel(e.target.value)}
            placeholder="channel"
            maxLength={30}
          />
          <button
            onClick={() => void create()}
            disabled={createLink.isPending || !destination.startsWith('https://')}
            className="px-3 py-2 rounded-md bg-primary text-primary-foreground text-sm disabled:opacity-50"
          >
            Create
          </button>
        </div>
      </section>

      {linksQuery.isLoading ? (
        <div className="text-muted-foreground">Loading…</div>
      ) : (linksQuery.data ?? []).length === 0 ? (
        <div className="rounded-lg border border-dashed border-border p-8 text-center text-muted-foreground text-sm">
          No tracked links yet.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-muted-foreground">
                <th className="px-4 py-2 font-medium">Redirect URL</th>
                <th className="px-4 py-2 font-medium">Destination</th>
                <th className="px-4 py-2 font-medium">Channel</th>
                <th className="px-4 py-2 font-medium" />
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {(linksQuery.data ?? []).map((link) => (
                <tr key={link.linkId} className="hover:bg-muted/50">
                  <td className="px-4 py-2 font-mono text-xs">
                    /adopt/r/{link.token}
                    <button
                      onClick={() => void copy(link.token)}
                      className="ml-2 text-primary hover:underline"
                    >
                      {copied === link.token ? 'copied' : 'copy'}
                    </button>
                  </td>
                  <td className="px-4 py-2 max-w-xs truncate">{link.destinationUrl}</td>
                  <td className="px-4 py-2">{link.channel ?? '—'}</td>
                  <td className="px-4 py-2 text-right">
                    <button
                      onClick={() => archiveLink.mutate(link.linkId)}
                      className="text-rose-600 hover:underline"
                    >
                      archive
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
